import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { MultiSelect } from './FormComponents/MultiSelect';
import { FileInput } from './FormComponents/FileInput';
import { LocationPicker } from '../../LocationPicker';
import { X, ChevronRight, ChevronLeft, Building2, MapPin, Home, Mail } from 'lucide-react';
import { venueTypes } from '../../../lib/constants/venueTypes';
import { amenities } from '../../../lib/constants/amenities';

import { supabase } from '../../../lib/supabase';
import type { Venue } from '../../../types/venues';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { useAdmin } from '../../../lib/hooks/useAdmin';

interface VenueFormProps {
  onClose: () => void;
  onSuccess: () => void;
  editVenue?: Venue | null;
}

type Step = 'basics' | 'location' | 'amenities' | 'media';

interface StepConfig {
  id: Step;
  title: string;
  icon: React.ComponentType<any>;
}

const steps: StepConfig[] = [
  { id: 'basics', title: 'Basic Info', icon: Building2 },
  { id: 'location', title: 'Location', icon: MapPin },
  { id: 'amenities', title: 'Features', icon: Home },
  { id: 'media', title: 'Media', icon: Mail },
];

export function VenueForm({ onClose, onSuccess, editVenue }: VenueFormProps) {
  const { t } = useTranslation();
  const { isAdmin } = useAdmin();
  const [currentStep, setCurrentStep] = useState<Step>('basics');
  const [formData, setFormData] = useState(() => {
    return {
      name: editVenue?.name || '',
      venue_type: editVenue?.venue_type || '',
      description: editVenue?.description || '',
      country: editVenue?.country || '',
      address: editVenue?.address || '',
      latitude: editVenue?.latitude || null as number | null,
      longitude: editVenue?.longitude || null as number | null,
      amenities: editVenue?.amenities || [] as string[],
      capacity: editVenue?.capacity?.toString() || '',
      available_for_rent: editVenue?.available_for_rent ?? false,
      price: editVenue?.price?.toString() || '',
      price_period: editVenue?.price_period || 'hour',
      currency: editVenue?.currency || 'EUR',
      contact_email: editVenue?.contact_email || '',
      contact_phone: editVenue?.contact_phone || '',
      sleeping_places: editVenue?.sleeping_places || '0',
      bedrooms: editVenue?.bedrooms || '0',
      bathrooms: editVenue?.bathrooms || '0',
      kitchens: editVenue?.kitchens || '0',
      claimable: editVenue?.claimable || false,
      create_community_on_approval: editVenue?.create_community_on_approval ?? false,
    };
  });
  const [images, setImages] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingImages, setExistingImages] = useState<string[]>(editVenue?.images || []);

  const isStepValid = () => {
    switch (currentStep) {
      case 'basics':
        return formData.name && formData.venue_type && formData.description;
      case 'location':
        return formData.latitude && formData.longitude && formData.capacity;
      case 'amenities':
        return true; // No longer required
      case 'media':
        return formData.contact_email; // Only email is required
    }
  };

  const handleNext = () => {
    if (!isStepValid()) {
      setError(t('forms.common.fillRequiredFields'));
      return;
    }
    
    const currentIndex = steps.findIndex(s => s.id === currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1].id);
    } else {
      handleSubmit();
    }
    setError('');
  };

  const handleBack = () => {
    const currentIndex = steps.findIndex(s => s.id === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1].id);
    }
    setError('');
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Upload images if any
      const imageUrls = await Promise.all(
        images.map(async (file) => {
          const { data, error } = await supabase.storage
            .from('venue-images')
            .upload(`${user.id}/${Date.now()}-${file.name}`, file);
          
          if (error) throw error;
          
          const { data: { publicUrl } } = supabase.storage
            .from('venue-images')
            .getPublicUrl(data.path);
          
          return publicUrl;
        })
      );

      // Prepare venue data
      const venueData = {
          user_id: user.id,
          name: formData.name,
          venue_type: formData.venue_type,
          description: formData.description,
          country: formData.country,
          address: formData.address,
          latitude: formData.latitude,
          longitude: formData.longitude,
          amenities: formData.amenities,
          capacity: parseInt(formData.capacity),
          available_for_rent: formData.available_for_rent,
          price: formData.available_for_rent && formData.price ? parseFloat(formData.price) : 0,
          price_period: formData.price_period,
          currency: formData.currency,
          images: [...existingImages, ...imageUrls],
          contact_email: formData.contact_email,
          contact_phone: formData.contact_phone || null,
          sleeping_places: formData.sleeping_places,
          bedrooms: formData.bedrooms,
          bathrooms: formData.bathrooms,
          kitchens: formData.kitchens,
          claimable: formData.claimable,
          create_community_on_approval: formData.create_community_on_approval,
      };

      let error;
      
      if (editVenue) {
        // Update existing venue
        const { error: updateError } = await supabase
          .from('venues')
          .update(venueData)
          .eq('id', editVenue.id);
        
        error = updateError;
      } else {
        // Create new venue
        const { error: insertError } = await supabase
          .from('venues')
          .insert([venueData]);
        
        error = insertError;
      }

      if (error) throw error;
      onSuccess();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'basics':
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">{t('forms.venue.venueName')}</Label>
              <Input
                id="name"
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t('forms.venue.enterVenueName')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="venue_type">Venue Type *</Label>
              <Select
                value={formData.venue_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, venue_type: value }))}
              >
                <SelectTrigger id="venue_type">
                  <SelectValue placeholder="Select venue type" />
                </SelectTrigger>
                <SelectContent>
                  {venueTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                required
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe your venue..."
                rows={4}
              />
            </div>

            {isAdmin && (
              <div className="p-4 bg-accent-base/10 rounded-lg border border-accent-text/20">
                <div className="flex items-center space-x-3">
                  <input
                    id="claimable"
                    type="checkbox"
                    checked={formData.claimable}
                    onChange={(e) => setFormData(prev => ({ ...prev, claimable: e.target.checked }))}
                    className="rounded border-gray-300 text-accent-text focus:ring-accent-text h-4 w-4"
                  />
                  <Label htmlFor="claimable" className="cursor-pointer font-normal">
                    <span className="font-semibold">Open for community claim</span>
                    <p className="text-sm text-content/60 font-normal">
                      Allow community members to claim ownership of this venue
                    </p>
                  </Label>
                </div>
              </div>
            )}

            <div className="p-4 bg-white rounded-lg border border-gray-100">
              <div className="flex items-start space-x-3">
                <input
                  id="create-community"
                  type="checkbox"
                  checked={formData.create_community_on_approval}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, create_community_on_approval: e.target.checked }))
                  }
                  className="mt-1 rounded border-gray-300 text-accent-text focus:ring-accent-text h-4 w-4"
                />
                <Label htmlFor="create-community" className="cursor-pointer font-normal">
                  <span className="font-semibold">Create a public community for this venue</span>
                  <p className="text-sm text-content/60 font-normal mt-1">
                    Your venue will be reviewed first. Once it&apos;s approved, we&apos;ll automatically create a public
                    community page and notify you. You can then go to your community and customize it.
                  </p>
                </Label>
              </div>
            </div>
          </div>
        );

      case 'location':
        return (
          <div className="space-y-6">
            <LocationPicker
              latitude={formData.latitude}
              longitude={formData.longitude}
              onChange={(lat, lng, address, countryCode) => setFormData(prev => ({
                ...prev,
                latitude: lat,
                longitude: lng,
                address: address || prev.address,
                country: countryCode || prev.country
              }))}
              label={t('forms.venue.venueLocation')}
              helperText={t('forms.venue.searchAddress')}
            />

            <div className="space-y-2">
              <Label htmlFor="capacity">{t('forms.venue.capacity')}</Label>
              <Input
                id="capacity"
                type="number"
                required
                min="1"
                value={formData.capacity}
                onChange={(e) => setFormData(prev => ({ ...prev, capacity: e.target.value }))}
                placeholder={t('forms.venue.maxCapacity')}
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  id="available-for-rent"
                  type="checkbox"
                  checked={formData.available_for_rent}
                  onChange={(e) => setFormData(prev => ({ ...prev, available_for_rent: e.target.checked }))}
                  className="rounded border-gray-300 text-accent-text focus:ring-accent-text h-4 w-4"
                />
                <Label htmlFor="available-for-rent" className="cursor-pointer font-normal">
                  {t('forms.venue.availableForRent')}
                </Label>
              </div>

              {formData.available_for_rent && (
                <div className="space-y-2 pl-7">
                  <Label>{t('forms.venue.pricing')}</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.price || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                      placeholder={t('forms.venue.amount')}
                    />
                    <Select
                      value={formData.currency}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EUR">EUR (€)</SelectItem>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="GBP">GBP (£)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={formData.price_period || 'hour'}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, price_period: value as 'hour' | 'day' | 'week' }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hour">{t('forms.venue.perHour')}</SelectItem>
                        <SelectItem value="day">{t('forms.venue.perDay')}</SelectItem>
                        <SelectItem value="week">{t('forms.venue.perWeek')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'amenities':
        return (
          <div className="space-y-6">
            <MultiSelect
              label={t('forms.venue.amenities')}
              options={amenities}
              selectedValues={formData.amenities}
              onChange={(values) => setFormData(prev => ({ ...prev, amenities: values }))}
              required
            />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sleeping_places">{t('forms.venue.sleepingPlaces')}</Label>
                <Input
                  id="sleeping_places"
                  type="number"
                  min="0"
                  value={formData.sleeping_places}
                  onChange={(e) => setFormData(prev => ({ ...prev, sleeping_places: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bedrooms">{t('forms.venue.bedrooms')}</Label>
                <Input
                  id="bedrooms"
                  type="number"
                  min="0"
                  value={formData.bedrooms}
                  onChange={(e) => setFormData(prev => ({ ...prev, bedrooms: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bathrooms">{t('forms.venue.bathrooms')}</Label>
                <Input
                  id="bathrooms"
                  type="number"
                  min="0"
                  value={formData.bathrooms}
                  onChange={(e) => setFormData(prev => ({ ...prev, bathrooms: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="kitchens">{t('forms.venue.kitchens')}</Label>
                <Input
                  id="kitchens"
                  type="number"
                  min="0"
                  value={formData.kitchens}
                  onChange={(e) => setFormData(prev => ({ ...prev, kitchens: e.target.value }))}
                />
              </div>
            </div>
          </div>
        );

      case 'media':
        return (
          <div className="space-y-6">
            <FileInput
              label={t('forms.venue.venueImages')}
              onChange={(files) => setImages(files)}
              maxFiles={5}
              accept="image/*"
              multiple
              description={t('forms.venue.uploadImages')}
            />

            {existingImages.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-content mb-2">{t('forms.venue.existingImages')}</h3>
                <div className="grid grid-cols-3 gap-2">
                  {existingImages.map((url, index) => (
                    <div key={index} className="relative">
                      <img
                        src={url}
                        alt={`Venue image ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => setExistingImages(prev => prev.filter((_, i) => i !== index))}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_email">{t('forms.venue.contactEmail')}</Label>
                <Input
                  id="contact_email"
                  type="email"
                  required
                  value={formData.contact_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact_phone">{t('forms.venue.contactPhone')}</Label>
                <Input
                  id="contact_phone"
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                />
              </div>
            </div>
          </div>
        );
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'basics': return t('forms.venue.basicInformation');
      case 'location': return t('forms.venue.locationPricing');
      case 'amenities': return t('forms.venue.amenitiesFeatures');
      case 'media': return t('forms.venue.mediaContact');
      default: return '';
    }
  };

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);

  const content = (
    <div className="fixed inset-0 min-h-[100dvh] bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-0 lg:p-4">
      <div className="bg-background rounded-none lg:rounded-lg w-full h-full min-h-[100dvh] lg:min-h-0 lg:h-auto lg:max-w-2xl lg:max-h-[90vh] overflow-hidden flex flex-col lg:shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-semibold text-content">
              {getStepTitle()}
            </h2>
            <p className="text-sm text-content/60 mt-1">{t('forms.common.stepOf', { current: currentStepIndex + 1, total: steps.length })}</p>
          </div>
          <button onClick={onClose} className="text-content/60 hover:text-content">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 bg-gray-50">
          <div className="flex gap-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`flex-1 h-2 rounded-full transition-colors ${
                  index <= currentStepIndex ? 'bg-accent-text' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">{error}</div>
          )}

          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 flex justify-between items-center">
          <button
            type="button"
            onClick={handleBack}
            disabled={currentStep === 'basics'}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              currentStep === 'basics'
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-content hover:bg-gray-100'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            {t('forms.common.previous')}
          </button>

          {currentStep !== 'media' ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={!isStepValid()}
              className={`flex items-center gap-2 px-6 py-2 rounded-full font-medium text-white transition-colors ${
                isStepValid()
                  ? 'bg-accent-text hover:bg-accent-text/90'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {t('forms.common.next')}
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleNext}
              disabled={loading || !isStepValid()}
              className={`flex items-center gap-2 px-6 py-2 rounded-full font-medium text-white transition-colors ${
                loading || !isStepValid()
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-accent-text hover:bg-accent-text/90'
              }`}
            >
              {loading ? (editVenue ? t('forms.common.updating') : t('forms.common.creating')) : (editVenue ? t('forms.venue.updateVenue') : t('forms.venue.createVenue'))}
            </button>
          )}
        </div>
      </div>
    </div>
  );
  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
}